/**
 * @file po-timeline-panel.tsx
 * @module app/modules/uProcure/po-management/po-timeline-panel
 * @description PO details: highlighted list of GRNs whose cost price differs from the PO, and the
 *   PO timeline (created, product / quantity / qty type changes, status changes, GRN cost).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useEffect, useState } from "react";
import APICallService from "../../../../api/apiCallService";
import { PURCHASEORDER } from "../../../../api/apiEndPoints";
import { POManagement } from "../../../../utils/constants";
import Method from "../../../../utils/methods";
import DraftPoTimeline from "./draft-po-timeline";
import { GrnCostChange, TimelineEvent } from "./draft-po.types";

const QTY: Record<number, string> = { 1: "Carton with Dozens", 2: "Dozen with Pieces", 3: "Piece", 4: "Carton with Pieces" };
const num = (n: number) => Number(n).toLocaleString("en-US");

const PoTimelinePanel = ({ poId, warehouse }: { poId?: string; warehouse?: string }) => {
  const [data, setData] = useState<{ records: TimelineEvent[]; costChanges: GrnCostChange[] } | null>(null);

  useEffect(() => {
    if (!poId) return;
    (async () => {
      const api = new APICallService(PURCHASEORDER.purchaseOrderTimeline, "", { id: poId, part: "timeline" }, "", "", "", POManagement, warehouse);
      const res = await api.callAPI();
      if (res && Array.isArray(res.records)) setData(res);
    })();
  }, [poId]);

  if (!data) return null;
  return (
    <div className="mt-7">
      {data.costChanges.length > 0 && (
        <div className="alert alert-danger mb-5">
          <div className="fs-16 fw-bolder mb-2">Cost price changed at GRN ({data.costChanges.length})</div>
          <table className="table table-sm mb-0 fs-14">
            <thead>
              <tr className="fw-bold">
                <th>GRN</th>
                <th>Product</th>
                <th>Qty Type</th>
                <th className="text-end">PO cost</th>
                <th className="text-end">GRN cost</th>
                <th className="text-end">Difference</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {data.costChanges.map((c, i) => (
                <tr key={i}>
                  <td className="fw-bold">{c.grnRefKey}</td>
                  <td>{c.title}</td>
                  <td>{QTY[c.quantityType] ?? c.quantityType}</td>
                  <td className="text-end">{num(c.poCost)}</td>
                  <td className="text-end fw-bold">{num(c.grnCost)}</td>
                  <td className={`text-end fw-bold ${c.grnCost > c.poCost ? "text-danger" : "text-success"}`}>
                    {c.grnCost > c.poCost ? "+" : ""}
                    {num(c.grnCost - c.poCost)}
                  </td>
                  <td>{c.date ? Method.convertDateToDDMMYYYYHHMMAMPM(c.date) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <DraftPoTimeline events={data.records} title="PO Timeline" />
    </div>
  );
};

export default PoTimelinePanel;
